import { useEffect, useRef, useState } from "react";
import { listPlans, listPublished } from "../builder/api.js";
import { Icon } from "../builder/components/ui.js";
import { addDays, localToday, mondayOf as mondayOfDate, weekRange } from "../builder/model/dates.js";

/** The Plan page's week arrows (DESIGN §17.6): the week is the address, so these navigate. */
export interface WeekNav {
  /** The Monday shown. */
  monday: string;
  prev: () => void;
  next: () => void;
  canNext: boolean;
  canPrev?: boolean;
  go: (monday: string) => void;
  /** The planner: this week's toolbar says how next week stands. */
  ahead?: boolean;
}

/** The week's dates under its title; on this week, for the planner, how next week stands. */
export function WeekDates({ monday, nav }: { monday: string; nav?: WeekNav | null }) {
  return (
    <span className="bp-dates num">
      {weekRange(monday)}
      {nav?.ahead && monday === mondayOfDate(localToday()) ? <NextWeekStatus nav={nav} /> : null}
    </span>
  );
}

type NextState = { text: string; tone: "published" | "draft" | "none" };

function NextWeekStatus({ nav }: { nav: WeekNav }) {
  const next = addDays(nav.monday, 7);
  const [state, setState] = useState<NextState | null>(null);
  useEffect(() => {
    let live = true;
    Promise.all([listPlans(), listPublished().catch(() => [])]).then(
      ([plans, published]) => {
        if (!live) return;
        const plan = plans.find((p) => p.week === next);
        if (plan) {
          if (!plan.publishedAt) setState({ text: "Draft, not published", tone: "draft" });
          else if (plan.changedSincePublish) setState({ text: "Published, changed since", tone: "draft" });
          else setState({ text: "Published", tone: "published" });
        } else if (published.some((w) => w.week === next)) setState({ text: "Published", tone: "published" });
        else setState({ text: "Not started", tone: "none" });
      },
      () => {},
    );
    return () => {
      live = false;
    };
  }, [next]);
  if (!state) return null;
  const icon = state.tone === "published" ? Icon.check : state.tone === "draft" ? Icon.edit : Icon.dot;
  return (
    <span className="bp-next-wrap">
      <span className="bp-next-sep" aria-hidden="true"> · </span>
      <button type="button" className={"bp-next-week is-" + state.tone} title={`Open next week, ${weekRange(next)}`} onClick={() => nav.go(next)}>
        {"Next week: "}
        {icon}
        <span className="bp-next-state">{state.text}</span>
      </button>
    </span>
  );
}

export function WeekArrows({ nav }: { nav: WeekNav }) {
  return (
    <div className="bp-week-nav" role="group" aria-label="Week">
      <button type="button" className="bp-icon-btn" aria-label="Previous week" title="Previous week" disabled={nav.canPrev === false} onClick={nav.prev}>
        {Icon.chevronLeft}
      </button>
      <button type="button" className="bp-icon-btn" aria-label="Next week" title="Next week" disabled={!nav.canNext} onClick={nav.next}>
        {Icon.chevron}
      </button>
      <WeekPicker nav={nav} />
    </div>
  );
}

interface WeekEntry {
  week: string;
  title: string;
  state: "draft" | "published" | "changed" | "final";
}

/** Every week written or published, newest first, so a week far away is one pick, not many arrows. */
function WeekPicker({ nav }: { nav: WeekNav }) {
  const [open, setOpen] = useState(false);
  const [weeks, setWeeks] = useState<WeekEntry[] | null>(null);
  const wrap = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const list = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    let live = true;
    Promise.all([listPlans().catch(() => []), listPublished().catch(() => [])]).then(([plans, published]) => {
      if (!live) return;
      const byWeek = new Map<string, WeekEntry>();
      for (const w of published) byWeek.set(w.week, { week: w.week, title: "", state: "published" });
      for (const p of plans) {
        const state = p.lockedAt ? "final" : !p.publishedAt ? "draft" : p.changedSincePublish ? "changed" : "published";
        byWeek.set(p.week, { week: p.week, title: p.title, state });
      }
      setWeeks([...byWeek.values()].sort((a, b) => (a.week < b.week ? 1 : -1)));
    });
    const away = (e: PointerEvent) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    };
    const id = setTimeout(() => window.addEventListener("pointerdown", away), 0);
    return () => {
      live = false;
      clearTimeout(id);
      window.removeEventListener("pointerdown", away);
    };
  }, [open]);
  useEffect(() => {
    if (open && weeks) list.current?.querySelector<HTMLElement>('[aria-current="true"], button')?.focus();
  }, [open, weeks]);
  const thisWeek = mondayOfDate(localToday());
  const pick = (monday: string) => {
    setOpen(false);
    if (monday !== nav.monday) nav.go(monday);
  };
  return (
    <div className="bp-week-picker" ref={wrap}>
      <button ref={button} type="button" className="bp-icon-btn" aria-label="Weeks" title="Go to a week" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        {Icon.calendar}
      </button>
      {open ? (
        <div
          ref={list}
          role="menu"
          className="bp-menu bp-weeks-menu"
          aria-label="Weeks"
          onKeyDown={(e) => {
            e.stopPropagation();
            const items = Array.from(list.current?.querySelectorAll<HTMLButtonElement>("button") ?? []);
            const i = items.indexOf(document.activeElement as HTMLButtonElement);
            if (e.key === "ArrowDown" || e.key === "ArrowUp") {
              e.preventDefault();
              items[(i + (e.key === "ArrowDown" ? 1 : -1) + items.length) % items.length]?.focus();
            } else if (e.key === "Escape" || e.key === "Tab") {
              e.preventDefault();
              setOpen(false);
              button.current?.focus();
            }
          }}
        >
          {nav.monday !== thisWeek ? (
            <button type="button" role="menuitem" tabIndex={-1} className="bp-menu-item bp-weeks-today" onClick={() => pick(thisWeek)}>
              <span>Go to this week</span>
            </button>
          ) : null}
          {!weeks ? (
            <p className="bp-muted bp-weeks-loading" role="status">
              Loading…
            </p>
          ) : weeks.length === 0 ? (
            <p className="bp-muted bp-weeks-loading">No weeks yet.</p>
          ) : (
            weeks.map((w) => (
              <button
                key={w.week}
                type="button"
                role="menuitem"
                tabIndex={-1}
                aria-current={w.week === nav.monday ? "true" : undefined}
                className={"bp-menu-item bp-weeks-item" + (w.week === nav.monday ? " is-current" : "")}
                onClick={() => pick(w.week)}
              >
                <span className="bp-weeks-name">
                  <span>{w.title || weekRange(w.week)}</span>
                  <span className="bp-weeks-dates num">
                    {w.title ? weekRange(w.week) : ""}
                    {w.week === thisWeek ? (w.title ? " · " : "") + "This week" : ""}
                  </span>
                </span>
                <span className={"bp-state is-" + w.state}>
                  {w.state === "draft" ? Icon.dot : w.state === "published" ? Icon.check : w.state === "final" ? Icon.lock : Icon.warning}
                  <span>{w.state === "draft" ? "Draft" : w.state === "published" ? "Published" : w.state === "final" ? "Final" : "Changed"}</span>
                </span>
              </button>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}

